import { Modal } from 'bootstrap';
import InvoiceForm from "@/controls/invoice";
import { hasPendingSubmit } from "@/controls/requestScope";

class DocumentForm {
    constructor() {
        this.uncompletedDocumentSelector = '#uncompletedDocument';
        this.modalOpen = false;
        // Inicializace při vytvoření instance
        this.initDocumentItem();
        this.initDocumentAutocomplete();
        this.initOnchangeLink();
        this.initDoubleClickHandler();
        this.preventMultipleEdits();
        this.protectNavigation();

        this.waitForInlineOperationsToFinish().then(() => {
            this.tryOpenInlineAdd();
        });

        this.lastQuery = '';
        this.activeRequest = null;
        this.isLoading = false;
        this.debounceTimer = null;
        this.requestSeq = 0;

        this.highlightSelected = false;
    }

    initDoubleClickHandler() {
        if (DocumentForm._dblClickBound) return;
        DocumentForm._dblClickBound = true;

        document.addEventListener('dblclick', (event) => {
            const row = event.target.closest('tr');
            if (!row) return;

            // Pokud je již nějaká editace otevřena (existuje submit tlačítko pro inline editaci), neděláme nic
            if (document.querySelector('input[name="inline_edit[submit]"]')) {
                return;
            }

            const editTrigger = row.querySelector('.datagrid-inline-edit-trigger');
            if (editTrigger) {
                // Zabráníme označování textu při dvojkliku
                window.getSelection().removeAllRanges();

                // Pokud je to Naja link (ajax), použijeme naja.uiHandler.clickElement
                if (editTrigger.classList.contains('ajax')) {
                    naja.uiHandler.clickElement(editTrigger);
                } else {
                    editTrigger.click();
                }
            }
        });
    }

    preventMultipleEdits() {
        // Guard: aby se listener nepřidal vícekrát
        if (DocumentForm._singleEditBound) return;
        DocumentForm._singleEditBound = true;

        const isEditActive = () => {
            // Zjistí, jestli je v DOMu tlačítko pro uložení inline editace
            return document.querySelector('input[name="inline_edit[submit]"]') !== null;
        };

        const isEditTrigger = (element) => {
            if (!element) return false;
            // Zkontrolujeme, zda se kliklo na editační tlačítko (nebo prvek uvnitř něj)
            return element.closest('.datagrid-inline-edit-trigger') ||
                (element.tagName === 'A' && element.href && element.href.includes('inlineEdit'));
        };

        // 1. Zablokování na úrovni DOM (fáze "capture")
        // Díky true na konci se tento listener provede jako úplně první na celé stránce
        document.addEventListener('click', (event) => {
            const trigger = isEditTrigger(event.target);
            if (trigger && isEditActive()) {
                event.preventDefault();     // Zabrání běžnému odkazu
                event.stopPropagation();    // Zabrání Naje nebo jiným knihovnám v reakci

                setTimeout(function() {
                    document.getElementById('loading-indicator').removeAttribute('style');
                }, 50);
            }
        }, true);

        naja.uiHandler.addEventListener('interaction', (event) => {
            if (isEditTrigger(event.detail.element) && isEditActive()) {
                event.preventDefault();
            }
        });
    }

    waitForInlineOperationsToFinish() {
        return new Promise(resolve => {
            const checkCondition = () => {
                // Čekáme jen na dokončení inline EDITACE – otevřený inline-add řádek
                // je cílový stav. Původní podmínka s || byla splněná skoro vždy,
                // takže se auto-otevírání spouštělo i během rozeditované položky
                // a zabíjelo uživateli editaci (ping-pong showInlineAdd ⇄ inlineEdit).
                if (document.querySelector('input[name="inline_edit[submit]"]') === null) {
                    resolve();
                } else {
                    setTimeout(checkCondition, 500);
                }
            };

            checkCondition();
        });
    }

    initDocumentItem() {
        // Načtení inputů pro položky faktury
        const documentItemCount = document.getElementById('document-item-count');
        const documentItemPrice = document.getElementById('document-item-price-purchase');
        const documentItemPriceBase = document.getElementById('document-item-price-purchase-base');

        if (documentItemCount && documentItemPrice && documentItemPriceBase) {
            documentItemPrice.addEventListener('input', () => {
                this.updateDocumentItemPrice(documentItemCount, documentItemPrice, documentItemPriceBase);
            });
        }
    }

    updateDocumentItemPrice(documentItemCount, documentItemPrice, documentItemPriceBase) {
        const itemCount = parseInt(documentItemCount.value, 10);
        const price = parseFloat(documentItemPrice.value);

        if (!isNaN(itemCount) && !isNaN(price)) {
            const priceBase = itemCount * price;

            documentItemPriceBase.value = priceBase.toFixed(2);
        }
    }

    initOnchangeLink() {
        document.querySelectorAll('[data-onchange-link]').forEach((element) => {
            const provide = element.getAttribute('data-onchange-link');
            if (!provide) return;

            if (element.classList.contains('onchange-initialized')) return;
            element.classList.add('onchange-initialized');

            let previousValue = element.value;

            const triggerRequest = (value) => {
                if (!value) return;

                naja.makeRequest('POST', provide, { value: value }, { history: false })
                    .then(response => {
                        if (response.note) {
                            $('#document-note').val(response.note).trigger('change');
                            $('#document-name').val(response.contactName).trigger('change');
                            $('#document-street').val(response.contactStreet).trigger('change');
                            $('#document-locality').val(response.contactLocality).trigger('change');
                            $('#document-postal').val(response.contactPostal).trigger('change');
                            $('#document-ico').val(response.contactIco).trigger('change');
                            $('#document-dic').val(response.contactDic).trigger('change');
                        }

                        this.tryOpenInlineAdd();
                    })
                    .catch(error => console.error('Error:', error));
            };

            // jQuery binding chytí i programové změny přes $(el).trigger('change')
            // (select2, restoreFormData), které nativní addEventListener nevidí.
            // Původní MutationObserver + 100ms polling hodnoty tu dělaly fantomové
            // requesty: restoreFormData po každém redraw formu tiše přepsalo hodnotu
            // přes .val() a poller na to reagoval dalším requestem → smyčka
            // (viz js-loop.log, setStoreFrom x9-11 za pár sekund).
            $(element).on('change.onchangeLink', () => {
                const newValue = element.value;
                if (newValue !== previousValue) {
                    previousValue = newValue;
                    triggerRequest(newValue);
                }
            });
        });
    }

    tryOpenInlineAdd() {
        const gridWrapper = document.querySelector('.datagrid-wrapper');
        const completeBtn = document.querySelector(this.uncompletedDocumentSelector);

        if (!gridWrapper || !completeBtn) return;

        if (gridWrapper.querySelector('.datagrid-row-inline-add')){
            const newInput = document.getElementById('invoice-item-name');
            if (newInput) {
                const active = document.activeElement;
                const isInputFocused = active && (
                    active.tagName === 'INPUT' ||
                    active.tagName === 'TEXTAREA' ||
                    active.isContentEditable
                );
                const isOtherInputFocused = active && active !== newInput && (
                    active.tagName === 'INPUT' ||
                    active.tagName === 'TEXTAREA' ||
                    active.tagName === 'SELECT' ||
                    active.isContentEditable
                );
                const isIframeInputOpen = !!document.querySelector('.iframe-input--open');

                if (!isInputFocused && !isOtherInputFocused && !isIframeInputOpen) {
                    newInput.focus();
                }

                if (window.innerWidth < 767) {
                    newInput.scrollIntoView({behavior: 'auto', block: 'start'});
                    const appWrapper = document.querySelector('.app-body');
                    appWrapper.scrollBy(0, -10);
                }
                // obs.disconnect();
            }
            return;
        }

        const showInlineAddLink = gridWrapper.querySelector('a[href*="showInlineAdd"]');
        if (showInlineAddLink) {
            // Dokud běží ukládání (submit formuláře), inline řádek neotevíráme –
            // showInlineAdd vystřelený souběžně se submitem dřív abortoval uložení
            // a spolkl redirect (tiket 854).
            if (hasPendingSubmit()) {
                return;
            }

            // Debounce přes modulový stav, ne přes atribut na odkazu – uzel odkazu
            // se při každém překreslení snippetu zahodí i s atributem, takže původní
            // pojistka nic nehlídala a klikalo se každou vteřinu dokola.
            const now = Date.now();
            if (now - (DocumentForm._lastInlineAddClickAt || 0) < 1000) {
                return;
            }
            DocumentForm._lastInlineAddClickAt = now;

            // Vlastní abort bucket: opakovaný auto-klik smí zrušit jen předchozí
            // auto-klik, nikdy cizí request (autocomplete, submit, signály gridu).
            showInlineAddLink.setAttribute('data-naja-unique', 'inline-add');
            naja.uiHandler.clickElement(showInlineAddLink);
        }

    }

    protectNavigation() {
        if (DocumentForm._navigationProtected) return;
        DocumentForm._navigationProtected = true;

        const isInsideSafeZone = (el) => {
            const completeBtn = document.querySelector(this.uncompletedDocumentSelector);
            const wrapper = completeBtn?.closest('form');

            return el.closest('.datagrid-wrapper') ||
                el.closest('.datagrid-row-inline-add') ||
                el.closest('.modal') ||
                (wrapper && wrapper.contains(el));
        };

        document.addEventListener('click', (event) => {
            if (event.__navigationChecked) return;
            event.__navigationChecked = true;

            const target = event.target.closest('a, button, input[type="submit"]');
            if (!target || this.modalOpen) return;

            const completeBtn = document.querySelector(this.uncompletedDocumentSelector);
            if (completeBtn && !isInsideSafeZone(target)) {
                const href = target.getAttribute('href');
                if (href || target.hasAttribute('data-href') || target.type === 'submit') {
                    event.preventDefault();
                    this.modalOpen = true;
                    this.showWarningDialog();
                }
            }
        });

        naja.uiHandler.addEventListener('interaction', (event) => {
            if (event.__navigationChecked) return;
            event.__navigationChecked = true;

            const element = event.detail.element;
            if (!element || this.modalOpen) return;

            const completeBtn = document.querySelector(this.uncompletedDocumentSelector);
            if (completeBtn && !isInsideSafeZone(element)) {
                event.preventDefault();
                this.modalOpen = true;
                this.showWarningDialog();
            }
        });
    }

    showWarningDialog() {
        //if (this.modalOpen) return; // Zabrání znovuotevření modalu
        const btnDocumentStorno = document.querySelector('.btn-document-storno');
        if (btnDocumentStorno) {
            btnDocumentStorno.click();
            document.addEventListener('hidden.bs.modal', () => {
                this.modalOpen = false;
            }, { once: true });
            return;
        }

        // původní řešení, pouze vyskakovací okno s nemožností ukončit doklad a odejít
        const modal = document.createElement('div');
        modal.classList.add('modal', 'fade');
        modal.tabIndex = -1;
        modal.role = 'dialog';
        modal.innerHTML = `
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title">Máte rozpracovaný doklad. Nejprve jej musíte stornovat pomocí tlačítka "Zrušit"</h4>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-footer d-flex justify-content-between">
                    <button type="button" class="btn btn-secondary">Zpět</button>
                </div>
            </div>
        </div>
        `;

        document.body.appendChild(modal);

        const bootstrapModal = new Modal(modal);

        modal.querySelector('.btn-secondary').addEventListener(
            'click', () => {
                bootstrapModal.hide();
                this.modalOpen = false;
            }
        );
        modal.addEventListener('hidden.bs.modal', () => {
            this.modalOpen = false;
            modal.remove();
        });

        this.modalOpen = true;
        bootstrapModal.show();
    }

    initDocumentAutocomplete() {
        this.documentContactInput = document.getElementById('document-contact');
        if (!this.documentContactInput || this.documentContactInput.classList.contains('autocomplete-attached')) return;

        this.documentContactInput.classList.add('autocomplete-attached');
        this.suggestionsContainer = document.getElementById('suggestions-container') || this.createSuggestionsContainer();

        let highlighted = null;
        let index = -1;
        // Upravená implementace pro klávesovou navigaci
        this.documentContactInput.addEventListener('keydown', event => {
            // Navigace pomocí šipek
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault(); // Zabránit posunu kurzoru v textovém poli

                if (this.suggestionsContainer.style.display === 'flex') {
                    var items = this.suggestionsContainer.querySelectorAll('.list-group-item');
                    if (items.length === 0) return;

                    highlighted = this.suggestionsContainer.querySelector('.list-group-item.highlighted');
                    index = -1;

                    if (highlighted) {
                        index = Array.from(items).indexOf(highlighted);
                        highlighted.classList.remove('highlighted');
                        highlighted.style.backgroundColor = 'rgb(232, 245, 252)';

                        if (event.key === 'ArrowDown') {
                            index = (index + 1) % items.length;
                        } else {
                            index = (index - 1 + items.length) % items.length;
                        }
                    } else {
                        index = event.key === 'ArrowDown' ? 0 : items.length - 1;
                    }

                    var newHighlighted = items[index];
                    newHighlighted.classList.add('highlighted');
                    //newHighlighted.style.background = '#f0f0f0';

                    // Zajistíme, že je vybraná položka viditelná
                    newHighlighted.scrollIntoView({block: 'nearest'});
                }
                return;
            }

            // Výběr položky pomocí klávesy Enter
            if (event.key === 'Enter') {
                highlighted = this.suggestionsContainer.querySelector('.list-group-item.highlighted');

                if (highlighted && this.suggestionsContainer.style.display === 'flex') {
                    event.preventDefault();
                    highlighted.click(); // Spustí kliknutí na zvýrazněnou položku
                    return;
                } else {
                    if ($('.disable-check-store-card-exists').length == 0) {
                        event.preventDefault();
                        this.invoiceItemCount.value = 1;
                        this.suggestionsContainer.innerHTML = '';
                        this.suggestionsContainer.style.display = 'none';
                        this.invoiceItemCount.select();
                        this.showAddProductModal();
                    }
                }
            }
        });

        this.documentContactInput.addEventListener('keyup', (event) => {
            // Přeskočit tyto klávesy, protože jsou zpracovány v keydown
            if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;

            this.loadSuggestions();
            this.highlightSelected = false;
        });

        this.documentContactInput.addEventListener('blur', () => {
            setTimeout(() => {
                if (!this.highlightSelected) {
                    // Nespouštět 'change' na ajax poli (způsobovalo AJAX submit formuláře),
                    // místo toho spustit checkVisibility ručně přes vlastní event
                    this.documentContactInput.dispatchEvent(new Event('visibility-check'));
                }

                this.suggestionsContainer.innerHTML = '';
                this.suggestionsContainer.style.display = 'none';
            }, 200);
        });
    }

    createSuggestionsContainer() {
        const container = document.createElement('div');
        container.id = 'suggestions-container';
        container.classList.add('list-group');
        container.style.position = 'absolute';
        container.style.zIndex = '9999';
        container.style.maxHeight = '35vh';
        container.style.height = 'auto';
        container.style.overflowY = 'auto';
        document.body.appendChild(container);
        return container;
    }

    // Naja si AbortController vytváří samo a ven ho vystavuje jen přes 'start' event.
    // Registrujeme se na něj jednou a controller si uložíme do objektu předaného
    // v options, ať víme, co rušit.
    static bindAutocompleteAbort() {
        if (naja.__autocompleteAbortBound) {
            return;
        }
        naja.addEventListener('start', (event) => {
            const {abortController, options} = event.detail;
            if (options.autocompleteAbortTarget) {
                options.autocompleteAbortTarget.controller = abortController;
            }
        });
        naja.__autocompleteAbortBound = true;
    }

    loadSuggestions() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            const query = this.documentContactInput.value;
            const provide = this.documentContactInput.getAttribute('data-provide');

            if (query.length <= 2) {
                this.suggestionsContainer.innerHTML = '';
                this.suggestionsContainer.style.display = 'none';
                return;
            }
            const isEan = (query.length === 13);

            // Každý dotaz dostane rostoucí pořadové číslo. Zobrazí se vždy jen odpověď
            // na aktuálně nejnovější dotaz – tím se žádný stisk klávesy neztratí a
            // zastaralá odpověď (např. na "mv-1", když už je v poli "mv-16") se zahodí.
            const requestId = ++this.requestSeq;

            // Samotné zahození odpovědi nestačí – server ji stejně celou dopočítá a protože
            // PHP drží na session exkluzivní zámek, řadí se requesty za sebe a ten poslední
            // (na kterém záleží) čeká na všechny předchozí. Zastaralý dotaz proto rovnou rušíme.
            DocumentForm.bindAutocompleteAbort();
            this.pendingSuggestRequest?.controller?.abort();
            const abortTarget = {};
            this.pendingSuggestRequest = abortTarget;

            // Novější dotaz => schovej dosud zobrazené (už zastaralé) možnosti a čekej
            // na svou vlastní odpověď.
            this.suggestionsContainer.innerHTML = '';
            this.suggestionsContainer.style.display = 'none';
            this.documentContactInput.classList.add('loading');

            const inputRect = this.documentContactInput.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const isBottomQuarter = inputRect.bottom > (viewportHeight * 0.60);

            if (isBottomQuarter) {
                this.suggestionsContainer.style.bottom = `${viewportHeight - (inputRect.top + window.scrollY)}px`;
                this.suggestionsContainer.style.top = 'auto';
            } else {
                this.suggestionsContainer.style.top = `${inputRect.bottom + window.scrollY}px`;
                this.suggestionsContainer.style.bottom = 'auto';
            }

            this.suggestionsContainer.style.left = `${inputRect.left + window.scrollX}px`;
            this.suggestionsContainer.style.width = `${inputRect.width * 4}px`;

            naja.makeRequest('POST', provide, { query }, { history: false, autocompleteAbortTarget: abortTarget })
                .then(response => {
                    // Zobraz jen odpověď na nejnovější dotaz a jen pokud se obsah pole mezitím nezměnil.
                    if (requestId !== this.requestSeq) return;
                    if (this.documentContactInput.value !== query) return;
                    this.displaySuggestions(response, query, isEan);
                })
                .catch(() => {})
                .finally(() => {
                    // 'loading' indikátor sundej jen když dobíhá poslední (nejnovější) dotaz
                    if (requestId === this.requestSeq) {
                        this.documentContactInput.classList.remove('loading');
                    }
                });
        }, 300);
    }

    displaySuggestions(suggestions, query, isEan) {
        this.suggestionsContainer.innerHTML = '';
        this.suggestionsContainer.style.display = 'flex';
        this.suggestionsContainer.style.flexDirection = 'column';

        Object.entries(suggestions).forEach(([id, product], index) => {
            if(index === "totalCount") return;
            const item = document.createElement('a');
            item.classList.add('list-group-item', 'list-group-item-action', 'ignore-form-dialog');
            if (index === 0){
                item.classList.add('highlighted');
                item.style.background = '#fff';
            }
            item.style.backgroundColor = 'rgb(232, 245, 252)';
            item.appendChild(this.prepareListGroupItem(product, query));

            item.addEventListener('click', () => {
                this.suggestionsContainer.innerHTML = '';
                this.suggestionsContainer.style.display = 'none';
                this.highlightSelected = true;

                this.documentContactInput.value = product.name;
                var documentName = document.getElementById('document-name');
                if (documentName) {
                    documentName.value = product.name;
                }
                var documentClient = document.getElementById('document-client');
                if (documentClient) {
                    documentClient.value = product.id;
                    documentClient.dispatchEvent(new Event('change'));

                    // Update links via signal
                    const updateLinksUrl = document.getElementById('update-customer-links-url');
                    if (updateLinksUrl) {
                        naja.makeRequest('GET', updateLinksUrl.value, { customerId: product.id }, { history: false });
                    }
                }
                var documentStoreCardId = document.getElementById('document-store-card-id');
                if (documentStoreCardId) {
                    const storeCardId = product.storeCardID ?? product.storeCardId ?? product.id ?? id;
                    documentStoreCardId.value = storeCardId ?? '';
                    documentStoreCardId.dispatchEvent(new Event('input'));
                    documentStoreCardId.dispatchEvent(new Event('change', { bubbles: true }));
                }
                var documentStreet = document.getElementById('document-street');
                if (documentStreet) {
                    documentStreet.value = product.street;
                }
                var documentLocality = document.getElementById('document-locality');
                if (documentLocality) {
                    documentLocality.value = product.locality;
                }
                var documentPostal = document.getElementById('document-postal');
                if (documentPostal) {
                    documentPostal.value = product.postal;
                }
                var documentIco = document.getElementById('document-ico');
                if (documentIco) {
                    documentIco.value = product.ico;
                }
                var documentDic = document.getElementById('document-dic');
                if (documentDic) {
                    documentDic.value = product.dic;
                }
                var maturityInput = document.getElementById('maturity');
                if (maturityInput) {
                    maturityInput.value = product.maturity;
                    maturityInput.dispatchEvent(new Event('input', { bubbles: true }));
                }
            });

            if(isEan){
                item.click();
                return;
            }
            this.suggestionsContainer.appendChild(item);
        });
    }

    prepareListGroupItem(product, query){
        const listGroupItem = document.createElement('span');
        var text = this.highlightMatch(product.name, query);
        if(product.street || product.locality || product.postal){
            text += `<br><small>`;
            if(product.street){
                text += `${product.street}`;
            }
            if(product.locality){
                text += `, ${product.locality}`;
            }
            if(product.postal){
                text += `, ${product.postal}`;
            }
            text += `</small>`;
        }
        if (product.ico || product.dic) {
            text += `<br><small>`;
            if (product.ico) {
                text += `<strong>IČO:</strong> ${product.ico}`;
            }
            if (product.dic) {
                text += `, <strong>DIČ:</strong> ${product.dic}`;
            }
            text += `</small>`;
        }
        if(product.purchasePrice || product.number || product.ean || product.snText || product.storeCardID){
            text += `<br><small>`;
            if(product.storeCardID){
                text += `<u>iKód:</u> ${product.storeCardID}`;
            }
            if(product.number){
                text += ` <u>Katalog:</u> ${product.number}`;
            }
            if(product.ean){
                text += ` <u>EAN:</u> ${product.ean}`;
            }
            if(product.snText){
                text += ` <u>VČ:</u> ${product.snText}`;
            }
            if(product.purchasePrice){
                var price = (parseFloat(product.purchasePrice)).toFixed(2);
                text += ` <u>Cena:</u> ${price} Kč`;
            }
            text += `</small>`;
        }
        listGroupItem.innerHTML = text;
        return listGroupItem;
    }

    highlightMatch(text, query) {
        return text.replace(new RegExp(`(${query})`, 'gi'), '<strong>$1</strong>');
    }

    static initialize() {
        // Vytvoření instance třídy pouze pokud jsou v DOM potřebné elementy
        return new DocumentForm();
    }
}

// Inicializace při načtení DOM
document.addEventListener('DOMContentLoaded', () => {
    DocumentForm.initialize();
});

// Připojení na události Naja pro AJAXové operace
naja.addEventListener('complete', (event) => {
    const response = event.detail && event.detail.response;
    if (!response || !response.ok) return;

    // Odpověď nese redirect – opouštíme stránku, re-inicializace (a hlavně
    // auto-otevření inline řádku) by se jen prala s odchodem ze stránky.
    if (event.detail.payload && event.detail.payload.redirect) return;

    DocumentForm.initialize();
});

export default DocumentForm;
